import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import React from "react";
import { pluralize } from "util/pluralize";

type SendPreview = inferProcedureOutput<AppRouter["homeCareLeadPartnerSend"]["preview"]>;

type SendSection = SendPreview["sections"][number];

type SendAlsoSending = NonNullable<SendPreview["alsoSending"]>;

const SendFieldRow: React.FC<{ label: string; value: string | null }> = ({
    label,
    value,
}) => (
    <div className="flex gap-3 py-1 text-sm">
        <dt className="w-28 shrink-0 text-gray-500 sm:w-36">{label}</dt>
        <dd className={value ? "text-gray-900" : "text-gray-400 italic"}>
            {value ?? "—"}
        </dd>
    </div>
);

const SendFieldListRow: React.FC<{ label: string; values: string[] }> = ({
    label,
    values,
}) => (
    <div className="flex gap-3 py-1 text-sm">
        <dt className="w-28 shrink-0 text-gray-500 sm:w-36">{label}</dt>
        {values.length > 0 ? (
            <dd className="text-gray-900">
                {values.map((value, index) => (
                    <div key={`${index}-${value}`}>{value}</div>
                ))}
            </dd>
        ) : (
            <dd className="text-gray-400 italic">—</dd>
        )}
    </div>
);

export const SendSectionList: React.FC<{ sections: SendSection[] }> = ({ sections }) => (
    <div className="space-y-4">
        {sections.map(section => (
            <section key={section.title}>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {section.title}
                </h3>
                <dl className="mt-1 divide-y divide-gray-100 border-t border-gray-100">
                    {section.fields.map(field => (
                        <SendFieldRow
                            key={field.label}
                            label={field.label}
                            value={field.value}
                        />
                    ))}
                </dl>
            </section>
        ))}
    </div>
);

const describeActivityCount = (activityCount: number): string | null =>
    activityCount > 0
        ? `${activityCount} ${pluralize(activityCount, "activity", "activities")}`
        : null;

export const SendRelatedRecords: React.FC<{
    referralSource: string | null;
    alsoSending: SendAlsoSending | null;
}> = ({ referralSource, alsoSending }) => (
    <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Related Records
        </h3>
        <dl className="mt-1 divide-y divide-gray-100 border-t border-gray-100">
            <SendFieldRow label="Referral source" value={referralSource} />
            {alsoSending ? (
                <>
                    <SendFieldListRow
                        label="Other contacts"
                        values={alsoSending.contactNames}
                    />
                    <SendFieldRow
                        label="Activities"
                        value={describeActivityCount(alsoSending.activityCount)}
                    />
                </>
            ) : null}
        </dl>
    </section>
);
